import { isEqual } from 'lodash'
import { PersistentStoreName } from 'stores/_utils/persistentStore/persistenceStoresCleanUpMap'
import { persistentInit } from '../_utils/persistentStore/persistentStore'
import {
  degreeDaysRecord,
  larvalTrendRecord,
  phenologyRecord,
  trapCatchesRecord,
} from './insectValueGroupRecords'

export const ValueGroupDictionary = {
  air_temperature: 'air_temperature',
  alternaria: 'alternaria',
  atmospheric_pressure: 'atmospheric_pressure',
  almond_bloom: 'almond_bloom',
  almond_hull_split: 'almond_hull_split',
  bee_hours: 'bee_hours',
  cherry_powdery_mildew: 'cherry_powdery_mildew',
  chill: 'chill',
  conditions: 'conditions',
  ...degreeDaysRecord((_, valueGroup) => valueGroup),
  dew_point: 'dew_point',
  equipment_status: 'equipment_status',
  evapotranspiration: 'evapotranspiration',
  fire_blight: 'fire_blight',
  fruit_growth: 'fruit_growth',
  grape_powdery_mildew: 'grape_powdery_mildew',
  humidity: 'humidity',
  irrigation_activity_pressure: 'irrigation_activity_pressure',
  irrigation_activity_zones: 'irrigation_activity_zones',
  irrigation_activity_flow_meters: 'irrigation_activity_flow_meters',
  ...larvalTrendRecord((_, valueGroup) => valueGroup),
  leaf_wetness: 'leaf_wetness',
  ndvi: 'ndvi',
  plant_stress: 'plant_stress',
  precipitation: 'precipitation',
  soil: 'soil',
  spray_conditions: 'spray_conditions',
  ...trapCatchesRecord((_, valueGroup) => valueGroup),
  ...phenologyRecord((_, valueGroup) => valueGroup),
  wet_bulb: 'wet_bulb',
  wind: 'wind',
  wind_machine: 'wind_machine',
} as const

export const SectionDictionary = {
  'crop-development': 'crop',
  'diseases': 'diseases',
  'environment': 'environment',
  'insects': 'insects',
  'water-management': 'water',
  'sprays': 'sprays',
  'equipment': 'equipment',
} as const

export type TValueGroup = keyof typeof ValueGroupDictionary

export type TLayer = TValueGroup | 'wind_machine_activity'

export type TSection = keyof typeof SectionDictionary

export const valueGroupToSectionDictionary: Record<TValueGroup, TSection> = {
  air_temperature: 'environment',
  atmospheric_pressure: 'environment',
  conditions: 'environment',
  dew_point: 'environment',
  humidity: 'environment',
  leaf_wetness: 'environment',
  precipitation: 'environment',
  wet_bulb: 'environment',
  wind: 'environment',
  ...degreeDaysRecord(() => 'insects'),
  ...larvalTrendRecord(() => 'insects'),
  ndvi: 'environment',
  ...trapCatchesRecord(() => 'insects'),
  ...phenologyRecord(() => 'insects'),
  irrigation_activity_pressure: 'water-management',
  irrigation_activity_zones: 'water-management',
  irrigation_activity_flow_meters: 'water-management',
  soil: 'water-management',
  evapotranspiration: 'water-management',
  fruit_growth: 'water-management',
  plant_stress: 'water-management',
  spray_conditions: 'sprays',
  alternaria: 'diseases',
  cherry_powdery_mildew: 'diseases',
  grape_powdery_mildew: 'diseases',
  fire_blight: 'diseases',
  bee_hours: 'crop-development',
  almond_bloom: 'crop-development',
  almond_hull_split: 'crop-development',
  chill: 'crop-development',
  equipment_status: 'equipment',
  wind_machine: 'equipment',
}

export const valueGroupsWithPrimaryAllowed: Record<TValueGroup, boolean> = {
  air_temperature: true,
  almond_bloom: true,
  almond_hull_split: true,
  alternaria: true,
  atmospheric_pressure: false,
  bee_hours: true,
  cherry_powdery_mildew: true,
  chill: true,
  conditions: false,
  ...degreeDaysRecord(() => true),
  dew_point: true,
  equipment_status: true,
  evapotranspiration: true,
  fire_blight: true,
  fruit_growth: true,
  grape_powdery_mildew: true,
  humidity: true,
  irrigation_activity_pressure: true,
  irrigation_activity_zones: true,
  irrigation_activity_flow_meters: true,
  ...larvalTrendRecord(() => false),
  leaf_wetness: true,
  ndvi: false,
  plant_stress: true,
  precipitation: true,
  soil: true,
  spray_conditions: true,
  ...trapCatchesRecord(() => true),
  ...phenologyRecord(() => true),
  wet_bulb: true,
  wind: true,
  wind_machine: true,
}

export const selectedValueGroupsStore = persistentInit<{
  primaryValueGroup: TValueGroup | null
  selectedValueGroups: Partial<Record<TValueGroup, number | false>>
}>({
  initialState: {
    primaryValueGroup: null,
    selectedValueGroups: {},
  },
  keysToPutInPersistentStorage: {
    primaryValueGroup: true,
    selectedValueGroups: true,
  },
  keysToPutInURL: {
    primaryValueGroup: true,
    selectedValueGroups: true,
  },
  storeName: PersistentStoreName.SELECTED_VALUE_GROUPS_STORE,
})

selectedValueGroupsStore.subscribeToChanges(
  (s) => s,
  (newStore, previousStore) => {
    // we are only concerned with selectedValueGroups changes
    if (isEqual(newStore.selectedValueGroups, previousStore.selectedValueGroups)) return

    const weSetAPrimaryValueGroupInThisChange = newStore.primaryValueGroup !== previousStore.primaryValueGroup

    // if we went out of our way to set a primary value group in this state update, we probably knew what we wanted
    if (weSetAPrimaryValueGroupInThisChange) {
      return
    }

    // if the user didn't have a manually overridden value group anyway, no need to do anything here
    if (!newStore.primaryValueGroup) {
      return
    }

    const keysThatAreOnAndWerentBefore = Object.keys(newStore.selectedValueGroups).filter(
      (k) =>
        !!newStore.selectedValueGroups[k as TValueGroup] &&
        !previousStore.selectedValueGroups[k as TValueGroup],
    )

    if (!!keysThatAreOnAndWerentBefore.length) {
      // if we turned on a new value group, let's not keep our overridden primary value group
      selectedValueGroupsStore.setState((s) => ({ ...s, primaryValueGroup: null }))

      return
    }

    if (!newStore.selectedValueGroups[newStore.primaryValueGroup]) {
      // if we removed the overridden primary value group from the selectedValueGroups as part of this state change, we should get rid of the override
      selectedValueGroupsStore.setState((s) => ({ ...s, primaryValueGroup: null }))

      return
    }
  },
)
